import React, { useMemo, useState } from 'react';
import { Download, Users } from 'lucide-react';
import {
  countryFlag, displayName, downloadCsv, fmtDate, humanize, isStaff, PLAN_LABEL, PLAN_TONE,
  planOf, ROLE_LABEL, timeAgo, useUsers,
} from './lib';
import {
  Avatar, Btn, EmptyState, ErrorState, Page, Pagination, Panel, Pill, RefreshButton, RowsSkeleton,
  SearchInput, Segmented, SortTH, sortRows, Table, TD, TH, TR, usePagination, useSort,
} from './ui';

const FILTERS = {
  all: () => true,
  premium: (u) => planOf(u) === 'premium' && !isStaff(u),
  free: (u) => planOf(u) === 'free' && !isStaff(u),
  trial: (u) => planOf(u) === 'trial' && !isStaff(u),
  expired: (u) => planOf(u) === 'expired' && !isStaff(u),
  staff: (u) => isStaff(u),
};
const FILTER_LABELS = { all: 'All', premium: 'Premium', free: 'Free', trial: 'Trial', expired: 'Expired', staff: 'Staff' };

const SORT_GETTERS = {
  name: (u) => displayName(u),
  created_at: (u) => u.created_at || '',
  last_action_date: (u) => u.last_action_date || '',
  plan: (u) => planOf(u),
};

export default function CustomersPanel({ onOpenCustomer }) {
  const { data: users, error, loading, refetch, updatedAt } = useUsers();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, onSort] = useSort('created_at', 'desc');
  const list = useMemo(() => users || [], [users]);

  const counts = useMemo(() => {
    const out = {};
    Object.entries(FILTERS).forEach(([key, fn]) => { out[key] = list.filter(fn).length; });
    return out;
  }, [list]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = list.filter(FILTERS[filter]).filter((u) => !q
      || (u.name || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
      || (u.user_id || '').toLowerCase().includes(q)
      || (u.partner_name || '').toLowerCase().includes(q)
      || (u.country || '').toLowerCase().includes(q));
    return sortRows(rows, sort, SORT_GETTERS);
  }, [list, filter, query, sort]);

  const pager = usePagination(filtered, 50, `${filter}|${query}|${sort.field}|${sort.dir}`);

  const exportCsv = () => downloadCsv(
    `customers_${new Date().toISOString().slice(0, 10)}.csv`,
    ['User ID', 'Name', 'Email', 'Plan', 'Role', 'Onboarded', 'Language', 'Country', 'Joined', 'Last gesture'],
    filtered.map((u) => [
      u.user_id, u.name, u.email, planOf(u), u.role || 'user', u.onboarding_completed ? 'yes' : 'no',
      u.language, u.country || u.country_code, u.created_at, u.last_action_date,
    ]),
  );

  const filterOptions = Object.keys(FILTERS)
    .filter((key) => ['all', 'premium', 'free', 'staff'].includes(key) || counts[key] > 0)
    .map((key) => ({ value: key, label: FILTER_LABELS[key], count: counts[key] }));

  return (
    <Page
      title="Customers"
      description={users ? `${list.length.toLocaleString('en-US')} accounts, ${counts.staff} of them staff (admins and beta testers).` : 'Every account in the app.'}
      actions={(
        <>
          <Btn variant="outline" size="sm" onClick={exportCsv} disabled={!filtered.length}>
            <Download className="h-3.5 w-3.5" /><span className="hidden sm:inline">Export CSV</span>
          </Btn>
          <RefreshButton onClick={refetch} loading={loading} updatedAt={updatedAt} />
        </>
      )}
    >
      {error && <ErrorState error={error} onRetry={refetch} />}

      <Panel flush>
        <div className="flex flex-col gap-3 border-b border-border p-3 sm:flex-row sm:items-center sm:justify-between">
          <SearchInput value={query} onChange={setQuery} placeholder="Name, email, ID, partner, country" />
          <Segmented value={filter} onChange={setFilter} options={filterOptions} />
        </div>

        {!users && loading ? (
          <RowsSkeleton rows={8} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Users}
            title={list.length ? 'No customers match' : 'No customers yet'}
            description={list.length ? 'Try another search or filter.' : undefined}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <SortTH label="Customer" field="name" sort={sort} onSort={onSort} />
                  <SortTH label="Plan" field="plan" sort={sort} onSort={onSort} />
                  <TH className="hidden md:table-cell">Onboarding</TH>
                  <TH className="hidden lg:table-cell">Country</TH>
                  <SortTH label="Last gesture" field="last_action_date" sort={sort} onSort={onSort} className="hidden md:table-cell" />
                  <SortTH label="Joined" field="created_at" sort={sort} onSort={onSort} align="right" />
                </tr>
              </thead>
              <tbody>
                {pager.slice.map((u) => {
                  const plan = planOf(u);
                  return (
                    <TR key={u.user_id} onClick={() => onOpenCustomer(u.user_id)}>
                      <TD>
                        <div className="flex min-w-0 items-center gap-3">
                          <Avatar user={u} size={32} />
                          <div className="min-w-0">
                            <p className="truncate font-medium text-foreground">{displayName(u)}</p>
                            <p className="truncate text-xs text-muted-foreground">{u.email || 'No email'}</p>
                          </div>
                        </div>
                      </TD>
                      <TD>
                        <div className="flex flex-wrap gap-1">
                          <Pill tone={PLAN_TONE[plan]}>{PLAN_LABEL[plan] || humanize(plan)}</Pill>
                          {u.role && u.role !== 'user' && <Pill tone="accent">{ROLE_LABEL[u.role] || u.role}</Pill>}
                        </div>
                      </TD>
                      <TD className="hidden md:table-cell">
                        {u.onboarding_completed
                          ? <span className="text-xs text-muted-foreground">Done</span>
                          : <Pill tone="warning">Not finished</Pill>}
                      </TD>
                      <TD className="hidden whitespace-nowrap text-xs text-muted-foreground lg:table-cell">
                        {u.country_code || u.country
                          ? <>{countryFlag(u.country_code)} {u.country || u.country_code}</>
                          : (u.language ? u.language.toUpperCase() : '—')}
                      </TD>
                      <TD className="hidden whitespace-nowrap text-xs text-muted-foreground md:table-cell">
                        {u.last_action_date ? timeAgo(u.last_action_date) : '—'}
                      </TD>
                      <TD align="right" className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(u.created_at)}</TD>
                    </TR>
                  );
                })}
              </tbody>
            </Table>
            <Pagination {...pager} noun="customers" />
          </>
        )}
      </Panel>
    </Page>
  );
}
